<template>
  <v-card elevation="0" outlined class="rounded-lg">
    <v-card-title class="d-flex align-center py-3 px-4">
      <v-icon small class="mr-2" color="deep-purple">mdi-magnify-scan</v-icon>
      <span class="body-1 font-weight-medium">Analyse QA</span>
      <v-spacer />
      <v-btn small outlined @click="newQaConversation">
        <v-icon small left>mdi-plus</v-icon>
        Nouvelle conversation
      </v-btn>
    </v-card-title>

    <v-card-text class="px-4 pb-4">
      <!-- Conversation chips -->
      <v-chip-group
        v-if="qaConversations.length"
        show-arrows
        class="mb-3"
        style="flex-wrap: nowrap; overflow-x: auto"
      >
        <v-chip
          v-for="conv in qaConversations"
          :key="conv.id"
          small
          :color="activeQaConvId === conv.id ? 'deep-purple' : ''"
          :dark="activeQaConvId === conv.id"
          :outlined="activeQaConvId !== conv.id"
          @click="selectQaConversation(conv.id)"
        >
          {{ (conv.title || conv.preview || 'Sans titre').slice(0, 30) }}
        </v-chip>
      </v-chip-group>

      <!-- Active conversation title + edit -->
      <div v-if="activeQaConvId" class="d-flex align-center mb-2" style="gap: 6px; min-height: 28px">
        <template v-if="!qaEditingTitle">
          <span class="text-subtitle-2">{{ displayTitle }}</span>
          <v-btn icon x-small @click="qaEditingTitle = true; qaTitleDraft = activeQaConversation.title">
            <v-icon x-small>mdi-pencil-outline</v-icon>
          </v-btn>
        </template>
        <template v-else>
          <v-text-field
            v-model="qaTitleDraft"
            dense
            hide-details
            autofocus
            style="max-width: 300px"
            @keydown.enter.prevent="saveQaTitle"
            @keydown.esc="qaEditingTitle = false"
            :append-icon="'mdi-check'"
            @click:append="saveQaTitle"
          />
        </template>
      </div>

      <!-- Message history -->
      <div
        ref="qaScroll"
        style="max-height: 400px; overflow-y: auto; display: flex; flex-direction: column; gap: 8px"
        class="mb-3"
      >
        <div
          v-if="!qaHistory.length && !activeQaConvId"
          class="caption grey--text text-center py-4"
        >
          Posez une question sur cette session…
        </div>

        <template v-for="(msg, i) in qaHistory">
          <!-- User message -->
          <div
            v-if="msg.role === 'user'"
            :key="'u' + i"
            class="d-flex justify-end"
          >
            <v-chip
              color="primary"
              dark
              style="max-width: 80%; white-space: normal; height: auto; padding: 6px 12px"
            >
              {{ msg.content }}
            </v-chip>
          </div>

          <!-- Assistant message -->
          <div
            v-else
            :key="'a' + i"
            class="body-2 qa-markdown"
            style="line-height: 1.6; max-width: 90%"
            v-html="renderMarkdown(msg.content, i === qaHistory.length - 1 && qaLoading)"
          />
        </template>
      </div>

      <!-- Preset chips -->
      <div class="d-flex flex-wrap mb-2" style="gap: 6px">
        <v-chip
          v-for="preset in qaPresets"
          :key="preset"
          x-small
          outlined
          @click="qaInput = preset"
        >{{ preset }}</v-chip>
      </div>

      <!-- Input -->
      <v-textarea
        v-model="qaInput"
        rows="2"
        auto-grow
        outlined
        dense
        hide-details
        placeholder="Votre question…"
        :disabled="qaLoading"
        @keydown.enter.exact.prevent="sendQaMessage"
      />
      <div class="d-flex justify-end mt-2">
        <v-btn
          color="deep-purple"
          dark
          small
          :loading="qaLoading"
          :disabled="!qaInput.trim()"
          @click="sendQaMessage"
        >
          Envoyer
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import MarkdownIt from 'markdown-it'

const md = new MarkdownIt({ breaks: true, linkify: true })

import {
  fetchAdminAgentSessionQAList,
  fetchAdminAgentSessionQAConversation,
  startAdminAgentSessionQA,
  continueAdminAgentSessionQA,
  updateAdminAgentSessionQATitle,
} from '@/services/adminApi'

export default {
  name: 'AdminAgentSessionQA',

  props: {
    sessionId: { type: [String, Number], required: true },
  },

  data() {
    return {
      qaConversations: [],
      activeQaConvId: null,
      qaHistory: [],
      qaInput: '',
      qaLoading: false,
      qaEditingTitle: false,
      qaTitleDraft: '',
      qaPresets: [
        'Pourquoi ce secteur ?',
        'Classification correcte ?',
        'Anomalies détectées ?',
        'Résumé des décisions',
      ],
    }
  },

  computed: {
    activeQaConversation() {
      return this.qaConversations.find(c => c.id === this.activeQaConvId) || null
    },

    displayTitle() {
      const conv = this.activeQaConversation
      if (!conv) return ''
      return conv.title || conv.preview || 'Sans titre'
    },
  },

  mounted() {
    this.loadQaConversations()
  },

  methods: {
    async loadQaConversations() {
      try {
        this.qaConversations = await fetchAdminAgentSessionQAList(this.sessionId)
        if (this.qaConversations.length) {
          await this.selectQaConversation(this.qaConversations[0].id)
        }
      } catch { /* silent */ }
    },

    async selectQaConversation(convId) {
      this.activeQaConvId = convId
      this.qaEditingTitle = false
      try {
        const data = await fetchAdminAgentSessionQAConversation(this.sessionId, convId)
        this.qaHistory = data.messages
        this.$nextTick(() => this.scrollQaToBottom())
      } catch { /* silent */ }
    },

    newQaConversation() {
      this.activeQaConvId = null
      this.qaHistory = []
      this.qaEditingTitle = false
    },

    async sendQaMessage() {
      const text = this.qaInput.trim()
      if (!text || this.qaLoading) return

      this.qaInput = ''
      this.qaLoading = true
      this.qaHistory.push({ role: 'user', content: text })
      this.qaHistory.push({ role: 'assistant', content: '' })
      this.$nextTick(() => this.scrollQaToBottom())

      try {
        let response
        if (!this.activeQaConvId) {
          response = await startAdminAgentSessionQA(this.sessionId, text)
        } else {
          response = await continueAdminAgentSessionQA(this.sessionId, this.activeQaConvId, text)
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buf = ''

        let chunk
        while (!(chunk = await reader.read()).done) {
          const { value } = chunk
          buf += decoder.decode(value, { stream: true })
          const lines = buf.split('\n')
          buf = lines.pop()
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            try {
              const data = JSON.parse(line.slice(6))
              if (data.text) {
                this.qaHistory[this.qaHistory.length - 1].content += data.text
                this.$nextTick(() => this.scrollQaToBottom())
              }
              if (data.done && data.conv_id && !this.activeQaConvId) {
                this.activeQaConvId = data.conv_id
                await this.loadQaConversations()
              }
            } catch { /* malformed frame */ }
          }
        }
      } catch (err) {
        this.qaHistory[this.qaHistory.length - 1].content = `Erreur : ${err.message}`
      } finally {
        this.qaLoading = false
      }
    },

    async saveQaTitle() {
      try {
        await updateAdminAgentSessionQATitle(this.sessionId, this.activeQaConvId, this.qaTitleDraft)
        const conv = this.qaConversations.find(c => c.id === this.activeQaConvId)
        if (conv) conv.title = this.qaTitleDraft
      } catch { /* silent */ }
      this.qaEditingTitle = false
    },

    renderMarkdown(content, showCursor) {
      const text = showCursor ? content + '▋' : content
      return md.render(text)
    },

    scrollQaToBottom() {
      const el = this.$refs.qaScroll
      if (el) el.scrollTop = el.scrollHeight
    },
  },
}
</script>

<style scoped>
.qa-markdown :deep(p) { margin: 0 0 6px; }
.qa-markdown :deep(p:last-child) { margin-bottom: 0; }
.qa-markdown :deep(ul),
.qa-markdown :deep(ol) { margin: 4px 0 6px 18px; padding: 0; }
.qa-markdown :deep(li) { margin-bottom: 2px; }
.qa-markdown :deep(code) { background: rgba(0,0,0,.06); padding: 1px 4px; border-radius: 3px; font-size: 0.85em; }
.qa-markdown :deep(pre) { background: rgba(0,0,0,.06); padding: 8px 10px; border-radius: 4px; overflow-x: auto; }
.qa-markdown :deep(pre code) { background: none; padding: 0; }
.qa-markdown :deep(blockquote) { border-left: 3px solid #bbb; margin: 6px 0; padding-left: 10px; color: #666; }
.qa-markdown :deep(h1), .qa-markdown :deep(h2), .qa-markdown :deep(h3) { margin: 8px 0 4px; font-size: 1em; font-weight: 600; }
</style>
